import React, { useEffect } from "react";
import { Meta } from "../layout/Meta";
import { SITE } from "../config/site";
import { PRODUCT_ASSETS } from "../content/productAssets";
import { productJsonLd, scrollToHash } from "../lib/productSeo";
import {
  ProductSection,
  ProductBreadcrumb,
  DisplayHeading,
  ProductArtwork,
  BenefitStrip,
  InkAppCta,
  IllustrativeNote,
} from "../components/product";

const PATH = "/building-strategies";
const TITLE = "Building Strategies";
const DESCRIPTION =
  "Turn entry and exit ideas into clear, testable rules without code. Save strategies and organize them with a Custom Score you control.";

const hero = PRODUCT_ASSETS.buildingStrategiesHero;
const scoringLeft = PRODUCT_ASSETS.buildingStrategiesScoringLeft;
const scoringRight = PRODUCT_ASSETS.buildingStrategiesScoringRight;

export default function BuildingStrategiesPage() {
  useEffect(() => {
    scrollToHash();
  }, []);

  return (
    <div className="w-full bg-bg0">
      <Meta
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        keywords={[
          "Building Strategies",
          "strategy builder",
          "no-code backtesting",
          "Custom Score",
          "saved strategies",
          "Stratify Backtesting",
        ]}
        jsonLd={productJsonLd({ title: TITLE, description: DESCRIPTION, path: PATH })}
      />

      <ProductSection className="overflow-hidden pt-10 md:pt-14">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-28 -z-10 mx-auto h-64 max-w-5xl opacity-40"
        >
          <img src="/images/hero-wave.png" alt="" className="h-full w-full object-contain" />
        </div>

        <ProductBreadcrumb current="Building Strategies" />

        <div className="mt-8 grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <DisplayHeading>
              Build it.
              <br />
              Without code.
            </DisplayHeading>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-muted">
              Turn entry and exit ideas into clear, testable rules.
            </p>
            <p className="mt-3 max-w-md text-sm text-muted">Plan. Test. Refine. Wherever you trade.</p>
            <div className="mt-8">
              <InkAppCta>Get the app</InkAppCta>
            </div>
          </div>
          <div className="min-w-0 lg:col-span-7">
            <ProductArtwork
              src={hero.src}
              width={hero.width}
              height={hero.height}
              alt={hero.alt}
              priority
            />
          </div>
        </div>

        <BenefitStrip
          className="mt-16"
          heading="An idea becomes a rule."
          items={[
            {
              n: "01",
              title: "Define your universe",
              body: "Choose a stock, timeframe, and backtest period.",
            },
            {
              n: "02",
              title: "Turn ideas into logic",
              body: "Combine indicators and conditions with simple, clear rules.",
            },
            {
              n: "03",
              title: "Test and refine",
              body: "Run the backtest engine, review results, and iterate with confidence.",
            },
          ]}
        />
      </ProductSection>

      <ProductSection id="saved-strategies">
        <div className="grid items-start gap-12 lg:grid-cols-12">
          <div className="min-w-0 lg:col-span-6">
            <DisplayHeading as="h2">
              Your strategies.
              <br />
              Your priorities.
            </DisplayHeading>
            <p className="mt-5 max-w-md text-lg leading-relaxed text-muted">
              Organize saved strategies with a score you customize.
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted">
              Weight return, risk and win rate to filter and organize strategies. Custom Score is
              a comparison tool, not a prediction.
            </p>
            <div className="mt-8 min-w-0">
              <ProductArtwork
                src={scoringLeft.src}
                width={scoringLeft.width}
                height={scoringLeft.height}
                alt={scoringLeft.alt}
              />
            </div>
          </div>
          <div className="min-w-0 lg:col-span-6">
            <ProductArtwork
              src={scoringRight.src}
              width={scoringRight.width}
              height={scoringRight.height}
              alt={scoringRight.alt}
              className="lg:max-w-none"
            />
            <IllustrativeNote className="mt-4">
              Example scores, returns, and validation labels are illustrative. They are not verified
              current results.
            </IllustrativeNote>
          </div>
        </div>

        <BenefitStrip
          className="mt-16"
          items={[
            {
              icon: "bookmark",
              title: "Save",
              body: "Keep your best ideas in one place for easy access.",
            },
            {
              icon: "gauge",
              title: "Score",
              body: "Weight return, risk and win rate to match your goals.",
            },
            {
              icon: "filter",
              title: "Filter",
              body: "Quickly find strategies that fit your criteria.",
            },
          ]}
        />

        <p className="mt-12 text-xs text-muted">
          Historical simulation. Not a forecast. Not financial advice.{" "}
          <a
            href={SITE.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-hint-deep underline-offset-4 hover:underline"
          >
            Download on the App Store
          </a>
          .
        </p>
      </ProductSection>
    </div>
  );
}
